<template>
  <div class="page-container">
    <div class="page-title-container">
      <h1 class="page-title">
        {{ editing ? "Editar contrato" : "Nuevo contrato" }}
      </h1>
    </div>
    <div>
      <form class="custom-form" @submit.prevent="save" novalidate>
        <b-card
          tag="article"
          no-body
          class="custom-card"
        >
          <b-card-header
            header-tag="section"
            class="custom-card-header d-flex align-items-center"
          >
            <h3>
              {{ editing ? "Datos del contrato" : "Datos del nuevo contrato" }}
            </h3>
          </b-card-header>

          <b-card-body class="custom-card-body p-0">
            <b-tabs card v-model="activeTab" class="contract-tabs">

              <!-- Tab 1: Datos del contrato -->
              <b-tab>
                <template #title>
                  <i class="ri-file-list-line mr-1"></i>Datos del contrato<span v-if="tabErrors[0]" class="tab-error-badge ml-1">•</span>
                </template>
                <ContractDataTab
                  :form="form"
                  :v="$v.form"
                  :contract-duration-list="contractDurationList"
                  :employee-list="employeeList"
                  :employee-info="employeeInfo"
                  :has-active-in-selected-company="hasActiveContractInSelectedCompany"
                  :other-active-companies="otherActiveContractsText"
                  :is-indefinite="isIndefiniteContract"
                  @employee-selected="onEmployeeSelected"
                  @probation-error="probationError = $event"
                />
              </b-tab>

              <!-- Tab 2: Ubicación laboral -->
              <b-tab>
                <template #title>
                  <i class="ri-building-line mr-1"></i>Ubicación laboral<span v-if="tabErrors[1]" class="tab-error-badge ml-1">•</span>
                </template>
                <WorkLocationTab
                  :form="form"
                  :v="$v.form"
                  :companies="companies"
                  :work-sites="workSites"
                  :areas="areas"
                  :positions="positions"
                  :employees="employees"
                  :schedules="schedules"
                  :channels="channels"
                  :expense-lines="expenseLines"
                  @company-changed="onCompanyChanged"
                />
              </b-tab>

              <!-- Tab 3: Condiciones laborales -->
              <b-tab>
                <template #title>
                  <i class="ri-money-dollar-circle-line mr-1"></i>Condiciones<span v-if="tabErrors[2]" class="tab-error-badge ml-1">•</span>
                </template>
                <ConditionsTab
                  :form="form"
                  :v="$v.form"
                  :dependent-summary="dependentSummary"
                  :employee-id="form.employeeId"
                  :payroll-types="payrollTypes"
                />
              </b-tab>

              <!-- Tab 4: Seguros y salud -->
              <b-tab>
                <template #title>
                  <i class="ri-heart-pulse-line mr-1"></i>Seguros y salud
                </template>
                <InsuranceTab
                  :form="form"
                  :v="$v.form"
                />
              </b-tab>

            </b-tabs>

            <div class="form-group-buttons text-right px-4 pb-4">
              <router-link
                :to="{ name: 'contracts-list' }"
                class="button button-light"
              >
                {{ editing ? "Volver" : "Cancelar" }}
              </router-link>
              <button
                class="button button-primary"
                type="submit"
                :disabled="(hasActiveContractInSelectedCompany && !editing) || probationError"
              >
                Guardar
              </button>
            </div>
          </b-card-body>
        </b-card>
      </form>
    </div>

  </div>
</template>

<script>
import { Alert } from 'wize-admin'
import * as moment from 'moment'
import { required, numeric, requiredIf } from 'vuelidate/lib/validators'

import {
  EmployeeService,
  CompanyService,
  AreaService,
  RolesService,
  WorkSiteService,
  ContractsService,
  WorkScheduleService,
  EmployeeDependentService,
  ChannelService,
  ExpenseLineService,
  PayrollTypeService,
} from '@/core/services'
import { Constants } from '@/core/utils'

import ContractDataTab  from './tabs/ContractDataTab.vue'
import WorkLocationTab  from './tabs/WorkLocationTab.vue'
import ConditionsTab    from './tabs/ConditionsTab.vue'
import InsuranceTab     from './tabs/InsuranceTab.vue'

export default {
  components: {
    ContractDataTab,
    WorkLocationTab,
    ConditionsTab,
    InsuranceTab,
  },

  data() {
    return {
      loading:   true,
      editing:   false,
      dependentSummary: { totalChildren: 0, qualifiedChildren: 0, hasQualifiedChildren: false, children: [] },
      activeTab: 0,
      probationError: false,

      // Catálogos
      employeeList: [],
      companies:    [],
      workSites:    [],
      areas:        [],
      positions:    [],
      employees:    [],
      schedules:    [],
      channels:     [],
      expenseLines: [],
      payrollTypes: [],

      contractDurationList: [
        Constants.ContractDurationOptions.ONE_MONTH,
        Constants.ContractDurationOptions.THREE_MONTHS,
        Constants.ContractDurationOptions.SIX_MONTHS,
        Constants.ContractDurationOptions.ONE_YEAR,
        Constants.ContractDurationOptions.INDEFINITE,
        Constants.ContractDurationOptions.POR_SERVICIO,
        Constants.ContractDurationOptions.PYME,
      ],

      // Info readonly del empleado seleccionado
      employeeInfo: {
        identifierType:  null,
        identifier:      null,
        fullName:        null,
        activeContracts: [],  // [{ id, companyId, companyName }]
      },

      form: {
        // Tab 1 — Datos del contrato
        employeeId:       null,
        contractType:     null,
        startDate:        null,
        finishDate:       null,
        probationEndDate: null,
        // Tab 2 — Ubicación laboral
        companyId:        null,
        workSiteId:       null,
        areaId:           null,
        positionId:       null,
        directManagerId:  null,
        scheduleId:       null,
        channelId:        null,
        expenseLineId:    null,
        // Tab 3 — Condiciones
        baseSalary:          null,
        salaryCurrency:      null,
        laborRegime:         null,
        occupationalCategory: null,
        workerType:          null,
        workSchedule:        null,
        weeklyHours:         null,
        hasFamilyAllowance:  false,
        specialSituation:    null,
        corporateEmail:      null,
        // Tab 4 — Seguros y salud
        hasEps:          false,
        epsEntity:       null,
        hasSctrPension:  false,
        hasSctrHealth:   false,
        hasLifeInsurance: false,
        essaludCode:     null,
        // Tipos de planilla
        payrollTypeIds:  [],
      },
    }
  },

  validations() {
    return {
      form: {
        employeeId:   { required },
        contractType: { required },
        startDate:    { required },
        finishDate:   { required: requiredIf(() => !this.isIndefiniteContract) },
        companyId:    { required },
        areaId:       { required },
        positionId:   { required },
        baseSalary:   { required, numeric },
        laborRegime:  { required },
      },
    }
  },

  computed: {
    isIndefiniteContract() {
      return this.form.contractType === Constants.ContractDurationOptions.INDEFINITE.key
    },
    tabErrors() {
      const v = this.$v.form
      return [
        v.employeeId.$error || v.contractType.$error || v.startDate.$error || v.finishDate.$error,
        v.companyId.$error  || v.areaId.$error        || v.positionId.$error,
        v.baseSalary.$error || v.laborRegime.$error,
        false,
      ]
    },
    hasActiveContractInSelectedCompany() {
      const list = this.employeeInfo.activeContracts || []
      const cid = this.form.companyId
      return !!cid && list.some(c => c.companyId === cid)
    },
    otherActiveContractsText() {
      const list = this.employeeInfo.activeContracts || []
      const cid = this.form.companyId
      const others = list.filter(c => c.companyId !== cid).map(c => c.companyName).filter(Boolean)
      return others.length ? others.join(', ') : ''
    },
  },

  watch: {
    'form.contractType': function () {
      if (this.isIndefiniteContract) {
        this.form.finishDate = null
      } else {
        this.setEndDate()
      }
    },
    'form.startDate': function (val) {
      if (val && val.length === 10 && !this.isIndefiniteContract) this.setEndDate()
    },
  },

  methods: {
    async onEmployeeSelected(employeeId) {
      this.form.employeeId = employeeId
      this.dependentSummary = { totalChildren: 0, qualifiedChildren: 0, hasQualifiedChildren: false, children: [] }
      if (!employeeId) {
        this.employeeInfo = { identifierType: null, identifier: null, fullName: null, activeContracts: [] }
        return
      }
      try {
        this.$store.dispatch('app/loading', true)
        const resp = (await EmployeeService.get(employeeId)).payload
        const list = Array.isArray(resp.currentContracts) ? resp.currentContracts : []
        this.employeeInfo = {
          identifierType:  resp.identifierType,
          identifier:      resp.identifier,
          fullName:        resp.fullName || `${resp.lastNamePaternal || ''} ${resp.lastNameMaternal || ''}, ${resp.name || ''}`.toUpperCase(),
          activeContracts: list.map(c => ({
            id:          c.id,
            companyId:   (c.company && c.company.id)   || c.companyId   || null,
            companyName: (c.company && c.company.name) || c.companyName || null,
          })),
        }
        // Load dependent summary for family allowance
        try {
          const summaryResp = await EmployeeDependentService.getSummary(employeeId)
          this.dependentSummary = summaryResp.payload || this.dependentSummary
          if (!this.editing && this.dependentSummary.hasQualifiedChildren) {
            this.form.hasFamilyAllowance = true
          }
        } catch (e) {
          // non-critical
        }
      } catch (ex) {
        Alert.error(ex.errors ? ex.errors.message : 'Error al cargar el colaborador')
        console.error(ex)
      } finally {
        this.$store.dispatch('app/loading', false)
      }
    },

    async onCompanyChanged(companyId) {
      this.workSites = []
      this.form.workSiteId = null
      if (!companyId) return
      try {
        const resp = (await WorkSiteService.listByCompany(companyId)).payload || []
        this.workSites = resp.map(w => ({ key: w.id, label: w.name }))
      } catch (ex) {
        console.error(ex)
      }
    },

    setEndDate() {
      if (!this.form.startDate) return
      this.form.finishDate = null
      let newDate = ''
      switch (this.form.contractType) {
        case Constants.ContractDurationOptions.ONE_MONTH.key:
          newDate = moment(this.form.startDate).add(1, 'month').subtract(1, 'day').format('YYYY-MM-DD')
          break
        case Constants.ContractDurationOptions.THREE_MONTHS.key:
          newDate = moment(this.form.startDate).add(3, 'months').subtract(1, 'day').format('YYYY-MM-DD')
          break
        case Constants.ContractDurationOptions.SIX_MONTHS.key:
          newDate = moment(this.form.startDate).add(6, 'months').subtract(1, 'day').format('YYYY-MM-DD')
          break
        case Constants.ContractDurationOptions.ONE_YEAR.key:
          newDate = moment(this.form.startDate).add(1, 'year').subtract(1, 'day').format('YYYY-MM-DD')
          break
        default:
          break
      }
      setTimeout(() => { this.form.finishDate = newDate }, 100)
    },

    async save() {
      this.$v.$touch()
      if (this.$v.$error) {
        const firstErrorTab = this.tabErrors.findIndex(e => e)
        if (firstErrorTab !== -1) this.activeTab = firstErrorTab
        setTimeout(() => {
          const el = document.querySelector('.form-error')
          if (el) this.$smoothScroll({ scrollTo: el, updateHistory: false, offset: -50 })
        }, 150)
        return
      }

      try {
        this.$store.dispatch('app/loading', true)
        const data = { ...this.form }
        let response
        if (!this.editing) response = await ContractsService.save(data)
        else response = await ContractsService.update(this.$route.params.id, data)
        if (response.status === 'BAD_REQUEST') {
          Alert.error('Error de validación: revise los campos')
          return
        }
        Alert.success('Contrato guardado correctamente')
        if (!this.editing) {
          this.$router.push({ name: 'contracts-list' })
        }
      } catch (ex) {
        console.error(ex)
        Alert.error(ex.errors ? ex.errors.message : 'Error al guardar')
      } finally {
        this.$store.dispatch('app/loading', false)
      }
    },

    async load(id) {
      try {
        this.$store.dispatch('app/loading', true)
        const payload = (await ContractsService.get(id)).payload
        this.form = {
          ...payload,
          employeeId:      payload.employee      ? payload.employee.id      : null,
          companyId:       payload.company       ? payload.company.id       : null,
          areaId:          payload.area          ? payload.area.id          : null,
          positionId:      payload.position      ? payload.position.id      : null,
          workSiteId:      payload.workSite      ? payload.workSite.id      : null,
          directManagerId: payload.directManager ? payload.directManager.id : null,
          scheduleId:      payload.schedule      ? payload.schedule.id      : null,
          channelId:       payload.channelId     || null,
          expenseLineId:   payload.expenseLineId || null,
          payrollTypeIds:  payload.payrollTypeIds || [],
        }
        this.editing = true
        if (this.form.companyId) await this.onCompanyChanged(this.form.companyId)
        if (this.form.employeeId) await this.onEmployeeSelected(this.form.employeeId)
      } catch (e) {
        console.error(e)
        Alert.error(e.errors ? e.errors.message : 'Error al cargar')
        this.$router.push({ name: 'contracts-list' })
      } finally {
        this.$store.dispatch('app/loading', false)
        setTimeout(() => { this.loading = false }, 1000)
      }
    },
  },

  async created() {
    if (this.$route.params.id) this.load(this.$route.params.id)
    else this.loading = false

    try {
      const [employees, companies, areas, positions, schedules, channelsResp, expenseLinesResp, payrollTypesResp] = await Promise.all([
        EmployeeService.listAll(),
        CompanyService.listAll(),
        AreaService.listAll(),
        RolesService.listAll(),
        WorkScheduleService.listActive(),
        ChannelService.listActive(),
        ExpenseLineService.listActive(),
        PayrollTypeService.listActive(),
      ])
      this.employeeList = (employees.payload || []).map(e => ({ key: e.id, label: e.description }))
      this.companies    = (companies.payload || []).map(c => ({ key: c.id, label: c.name }))
      this.areas        = (areas.payload    || []).map(a => ({ key: a.id, label: a.description }))
      this.positions    = (positions.payload || []).map(p => ({ key: p.id, label: p.description }))
      this.schedules    = (schedules.payload || []).map(s => ({ key: s.id, label: s.name, ...s }))
      this.channels     = (channelsResp.payload || []).map(c => ({ key: c.id, label: c.name }))
      this.expenseLines = (expenseLinesResp.payload || []).map(e => ({ key: e.id, label: e.name }))
      this.payrollTypes = payrollTypesResp.payload || []
      // Lista de empleados para jefe directo (misma fuente)
      this.employees    = this.employeeList
    } catch (ex) {
      console.error(ex)
      Alert.error('Error al cargar los catálogos')
    }
  },
}
</script>

<style lang="stylus" scoped>
.contract-tabs
  ::v-deep .nav-tabs
    border-bottom: 1px solid #dee2e6
    flex-wrap: nowrap
    overflow-x: auto
    .nav-link
      white-space: nowrap

.tab-error-badge
  color: #dc3545
  font-size: 1.1rem
  line-height: 1
  vertical-align: middle
</style>
